<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import ZinputBreak from '../_common/z/zinput-break.svelte'
    import ZinputTitle from '../_common/z/zinput-title.svelte'
    import Zdetails from '../_common/z/zdetails.svelte'
    import { DetailsClass, ZDetailItem } from '../_common/z/zgrid'
    import { faFloppyDisk, faArrowRotateLeft, faBoltLightning } from '@fortawesome/free-solid-svg-icons'
    import type { Purchase } from '../../modules/paymentsApi/purchases/purchase.entity'
    import {
        PriceIndex,
        PriceIndexFriendly,
        PRICE_ADJUSTMENT_MIN_MONTHS,
        type PriceAdjustmentPreviewDto,
        type RevertPriceAdjustmentPreviewDto,
    } from '../../modules/paymentsApi/purchases/dto/price-adjustment.dto'
    import dayjs from 'dayjs'
    import Zframe from '../_common/z/zframe.svelte'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    export let purchaseId: string = null

    let purchase: Purchase = null
    let preview: PriceAdjustmentPreviewDto = null
    let revert: RevertPriceAdjustmentPreviewDto = null

    // configuração editável
    let auto = false
    let index: PriceIndex = PriceIndex.ipca
    // O percentual nasce do índice e fica editável: o acumulado nem sempre é o que foi negociado, e
    // um contrato atrasado precisa de correção maior do que a janela de 12 meses cobre.
    let percent: number = null

    let show = false
    let salvando = false
    let aplicando = false
    let revertendo = false

    const opcoesIndice = Object.values(PriceIndex).map((el) => ({ value: el, label: PriceIndexFriendly[el] }))

    onMount(async () => {
        await carregar()
    })

    async function carregar() {
        try {
            show = true
            purchase = await window.api.payments.purchases.getOne(purchaseId)
            auto = !!purchase.priceAdjustmentAuto
            if (purchase.priceAdjustmentIndex) index = purchase.priceAdjustmentIndex as PriceIndex
            await recarregarPreview()
            revert = await window.api.payments.purchases.previewRevertPriceAdjustment(purchaseId)
        } catch (error) {
            window.error(error.message || error)
        } finally {
            show = false
        }
    }

    // O preview vem do backend, e não de conta feita aqui: é a mesma rotina que aplica o reajuste,
    // então o que a tela promete e o que o sistema faz não têm como divergir.
    async function recarregarPreview() {
        preview = await window.api.payments.purchases.previewPriceAdjustment(purchaseId, index)
        percent = preview?.percentSuggested ?? null
    }

    async function trocarIndice() {
        try {
            show = true
            await recarregarPreview()
        } catch (error) {
            window.error(error.message || error)
        } finally {
            show = false
        }
    }

    async function salvarConfig() {
        try {
            salvando = true
            await window.api.payments.purchases.setPriceAdjustmentConfig(purchaseId, {
                priceAdjustmentAuto: auto,
                priceAdjustmentIndex: auto ? index : undefined,
            })
            await carregar()
        } catch (error) {
            window.error(error.message || error)
        } finally {
            salvando = false
        }
    }

    async function aplicarAgora() {
        if (!preview?.allowed) return
        if (!confirm(`Reajustar de ${moeda(preview.currentValue)} para ${moeda(novoValor)}?`)) return
        try {
            aplicando = true
            await window.api.payments.purchases.applyPriceAdjustment(purchaseId, { index, percent })
            await carregar()
            dispatch('applied')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            aplicando = false
        }
    }

    async function anular() {
        if (!revert?.allowed) return
        if (!confirm(`Anular o último reajuste e voltar o valor para ${moeda(revert.restoredValue)}?`)) return
        try {
            revertendo = true
            await window.api.payments.purchases.revertPriceAdjustment(purchaseId)
            await carregar()
            dispatch('applied')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            revertendo = false
        }
    }

    function moeda(valor: number): string {
        return window.api.utils.utils.toReal(valor || 0)
    }

    function mesLegivel(mes: string): string {
        return mes ? dayjs(mes + '-01').format('MMM/YYYY') : ''
    }

    // 'auto' entra como parâmetro para o bloco reativo enxergá-lo como dependência: o Svelte não
    // olha dentro da função para montar a lista, e sem isso o próximo reajuste não apareceria ao
    // ligar o toggle
    $: details = new DetailsClass({ items: purchase ? buildDetailItems(purchase, auto) : [] })

    function buildDetailItems(p: Purchase, autoLigado: boolean): ZDetailItem[] {
        const items = [
            new ZDetailItem({ label: 'Valor atual', content: moeda(p.purchaseTotalValue), size: 4 }),
            new ZDetailItem({
                label: 'Último reajuste',
                content: p.lastPriceAdjustmentAt ? dayjs(p.lastPriceAdjustmentAt).format('DD/MM/YYYY') : 'nunca reajustado',
                size: 4,
            }),
        ]
        // só faz sentido anunciar a próxima data quando existe alguém para cumpri-la
        if (autoLigado && p.nextPriceAdjustmentAt) {
            items.push(
                new ZDetailItem({
                    label: 'Próximo reajuste',
                    content: dayjs(p.nextPriceAdjustmentAt).format('DD/MM/YYYY'),
                    size: 4,
                }),
            )
        }
        return items
    }

    // o novo valor acompanha o percentual editado, sem ida ao servidor a cada tecla
    $: novoValor = percent > 0 ? Math.round(preview?.currentValue * (1 + percent / 100) * 100) / 100 : preview?.currentValue
    // reajuste negativo não reduz preço: deflação é no-op, e a tela precisa dizer isso
    $: semAumento = !(percent > 0)
</script>

<div class="root">
    <Zact>
        <div class="spacer" />
        <Zbutton color="secondary" icon={faFloppyDisk} label="Salvar" loading={salvando} disabled={salvando} on:click={salvarConfig} />
        {#if preview?.allowed}
            <Zbutton color="primary" icon={faBoltLightning} label="Aplicar agora" loading={aplicando} disabled={aplicando} on:click={aplicarAgora} />
        {/if}
    </Zact>

    {#if preview && !preview.allowed}
        <!-- o bloqueio de 12 meses é lei, então a tela explica em vez de só desabilitar o botão -->
        <div class="alert alert-warning m-3 mb-0">{preview.reason}</div>
    {/if}

    <Zframe class="m-3">
        <ZContainer gapRow="8px" gapCol="8px">
            <ZinputTitle title="Reajuste automático"></ZinputTitle>
            <Zinput size={6} sizeM={12} label="Reajustar automaticamente a cada {PRICE_ADJUSTMENT_MIN_MONTHS} meses" type="toggle" bind:value={auto} />
            <Zinput size={6} sizeM={12} label="Índice" type="select" options={opcoesIndice} bind:value={index} on:change={trocarIndice} />
            <ZinputBreak></ZinputBreak>
        </ZContainer>

        {#if purchase}
            <Zdetails {details} />
        {/if}

        {#if preview?.allowed}
            <ZContainer gapRow="8px" gapCol="8px">
                <ZinputTitle title="Aplicar agora"></ZinputTitle>
                <Zinput size={6} sizeM={12} label="Percentual a aplicar (%)" type="number" bind:value={percent} />
                <Zinput size={6} sizeM={12} label="Novo valor" type="text" value={moeda(novoValor)} disabled={true} />
            </ZContainer>

            {#if preview.windowStart}
                <div class="nota">
                    {PriceIndexFriendly[preview.index]} acumulado de {mesLegivel(preview.windowStart)} a {mesLegivel(preview.windowEnd)}:
                    <strong>{preview.percentSuggested}%</strong>
                </div>
            {:else}
                <div class="alert alert-warning mt-3 mb-0">Não foi possível obter o índice agora. Informe o percentual manualmente ou tente mais tarde.</div>
            {/if}

            {#if preview.windowShorterThanElapsed}
                <!-- a janela é de 12 meses, mas passou mais tempo: quem decide o que fazer é o operador -->
                <div class="alert alert-warning mt-3 mb-0">
                    Faz {preview.monthsSinceLast} meses desde o último reajuste, e o índice acima cobre {PRICE_ADJUSTMENT_MIN_MONTHS}. Ajuste o percentual se o
                    contrato previr a diferença.
                </div>
            {/if}

            {#if semAumento}
                <div class="alert alert-secondary mt-3 mb-0">Percentual sem aumento: o valor não será alterado. Reajuste negativo não reduz o preço.</div>
            {/if}

            {#if preview.chargesToUpdate.length}
                <div class="nota">
                    {preview.chargesToUpdate.length} cobrança(s) já agendada(s) passam a valer o novo preço.
                </div>
            {/if}
            {#if preview.chargesUntouched.length}
                <!-- perto do vencimento não muda de valor: o cliente não teria tempo de perceber. o novo
                 preço entra na próxima cobrança gerada, então o reajuste atrasa no máximo um ciclo -->
                <div class="alert alert-secondary mt-3 mb-0">
                    {preview.chargesUntouched.length} cobrança(s) vencem em menos de 20 dias e mantêm o valor atual. O novo preço vale a partir da cobrança seguinte.
                </div>
            {/if}
        {/if}

        <!-- assinatura nunca reajustada não mostra nem o título: não há ação sem objeto -->
        {#if revert?.hasAdjustment}
            <ZContainer gapRow="8px" gapCol="8px">
                <ZinputTitle title="Anular último reajuste"></ZinputTitle>
            </ZContainer>
            {#if revert.allowed}
                <div class="linha">
                    <span>Aplicado em {dayjs(revert.appliedAt).format('DD/MM/YYYY')} ({revert.percent}%)</span>
                    <strong>volta para {moeda(revert.restoredValue)}</strong>
                </div>
                {#if revert.chargesNotRestorable.length}
                    <!-- já saiu para o gateway: existe boleto emitido ou pix pago com o valor novo, e
                     nenhuma alteração local desfaz isso -->
                    <div class="alert alert-warning mt-3 mb-0">
                        {revert.chargesNotRestorable.length} cobrança(s) já foram emitidas com o valor reajustado e não podem voltar atrás. Só as ainda agendadas
                        serão corrigidas.
                    </div>
                {/if}
                <Zact>
                    <div class="spacer" />
                    <Zbutton color="red" icon={faArrowRotateLeft} label="Anular reajuste" loading={revertendo} disabled={revertendo} on:click={anular} />
                </Zact>
            {:else}
                <div class="alert alert-secondary mt-3 mb-0">{revert.reason}</div>
            {/if}
        {/if}
    </Zframe>

    <LoadSpinner {show} />
</div>

<style>
    .linha {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding: 6px 0;
        border-bottom: 1px solid rgba(128, 128, 128, 0.25);
    }
    .nota {
        margin-top: 10px;
        font-size: 13px;
        opacity: 0.85;
    }
</style>
